import { useRef, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useLocation } from "wouter"
import { CreateFormModuleSchema, type CreateFormModule } from "shared"
import cx from "clsx"
import { isExpectedApiError } from "@rawa/api-client/ApiResponseError"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { trackError } from "@rawa/telemetry"
import { createFormModule, fetchEntityLinks } from "../api"
import { MODULE_NAMES, useNameTaken } from "../hooks/useNameTaken"
import { ModuleNameFields } from "../components/ModuleNameFields"
import { pathHelpers, paths } from "../../../paths"
import typography from "@rawa/ui/styles/typography.module.css"
import { AdminButton } from "@rawa/ui/AdminButton"
import { DiscardChangesDialog } from "../components/DiscardChangesDialog"
import styles from "./NewModulePage.module.css"

export function NewModulePage() {
  const [, navigate] = useLocation()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [entityLink, setEntityLink] = useState("")
  const [leaving, setLeaving] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const trimmedName = name.trim()
  const trimmedDescription = description.trim()
  const nameTaken = useNameTaken(MODULE_NAMES, name)

  const { data: entityLinks = [] } = useQuery({
    queryKey: ["form-module-entity-links"],
    queryFn: ({ signal }) => fetchEntityLinks(signal),
    retry: false,
    meta: { source: "fetchEntityLinks" },
  })

  // Next is unavailable rather than answerable: with three required fields and
  // the markers beside them, a rejection after the fact says nothing the form
  // has not already said. The schema that owns the rules decides — trimmed,
  // within its lengths — and what it accepts is what is sent.
  const parsed = CreateFormModuleSchema.safeParse({
    name,
    description,
    entityLink,
  })
  const complete = parsed.success && !nameTaken

  // Leaving is only a question while something entered would be lost.
  const changed =
    trimmedName !== "" || trimmedDescription !== "" || entityLink !== ""
  const leave = () => (changed ? setLeaving(true) : navigate(paths.modules))

  const create = useMutation({
    mutationFn: (formModule: CreateFormModule) => createFormModule(formModule),
    onSuccess: (created) => navigate(pathHelpers.toModule(created.id)),
    onError: (err) => {
      // A 4xx carries a reason the administrator can act on and is shown as
      // sent below; only an unexpected failure is a fault worth recording.
      if (!isExpectedApiError(err)) {
        trackError(err, { source: "createFormModule" })
      }
    },
  })
  const error = create.error
    ? isExpectedApiError(create.error)
      ? create.error.message
      : "Could not create this module. Please try again."
    : null

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={leave}>
        ← Back to modules
      </button>

      <div className={styles.titleBlock}>
        <p className={styles.eyebrow}>Module builder</p>
        {/* The name as it is typed, so the page says which module is being
            built before it exists. */}
        <h1 className={cx(typography.h2, styles.heading)}>
          {trimmedName === "" ? "New module" : trimmedName}
        </h1>
        <p className={styles.subtitle}>
          Build a reusable module once, then use it on any form.
        </p>
      </div>

      {error && <ErrorCallout>{error}</ErrorCallout>}

      <div className={styles.card}>
        <aside className={styles.explainer}>
          <p className={styles.eyebrow}>Module setup</p>
          <h2 className={cx(typography.h5, styles.explainerHeading)}>
            Define the reusable module
          </h2>
          <p className={styles.explainerBody}>
            Choose what this module&apos;s answers attach to.
          </p>
        </aside>

        <div className={styles.panel}>
          <ModuleNameFields
            name={name}
            description={description}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            nameTaken={nameTaken}
          />

          <div className={styles.field}>
            <label className={styles.label} htmlFor="module-entity-link">
              Entity Link
              <span aria-hidden="true" className={styles.required}>
                {" "}
                *
              </span>
            </label>
            <select
              id="module-entity-link"
              name="module-entity-link"
              className={styles.select}
              value={entityLink}
              onChange={(e) => setEntityLink(e.target.value)}
              required
            >
              <option value="">Select an entity link</option>
              {entityLinks.map((link) => (
                <option key={link.key} value={link.key}>
                  {link.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.actions}>
            <AdminButton variant="outline" ref={cancelRef} onClick={leave}>
              Cancel
            </AdminButton>
            <AdminButton
              // aria-disabled leaves the button clickable, so the guard is here.
              onClick={() =>
                parsed.success && complete && create.mutate(parsed.data)
              }
              disabled={create.isPending}
              ariaDisabled={!complete}
              describedBy={!complete ? "next-hint" : undefined}
            >
              {create.isPending ? (
                "Saving…"
              ) : (
                <>
                  Next
                  {/* aria-hidden so the button is still named just "Next". */}
                  <span aria-hidden="true"> →</span>
                </>
              )}
            </AdminButton>
            {!complete && (
              <span id="next-hint" className={styles.srOnly}>
                {nameTaken
                  ? "Choose a different module name to continue."
                  : "Enter a name, description and entity link to continue."}
              </span>
            )}
          </div>
        </div>
      </div>
      <DiscardChangesDialog
        open={leaving}
        onKeepEditing={() => {
          setLeaving(false)
          // Back where the question came from, so the keyboard does not start
          // over at the top of the page.
          cancelRef.current?.focus()
        }}
        onDiscard={() => navigate(paths.modules)}
      />
    </div>
  )
}
